import Axios from 'api/client';
import { Widget, WidgetType } from 'types/domain';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

interface WidgetsParamsAccountId {
    accountId: number;
}
interface WidgetsParamsWidgetId {
    widgetId: number;
}
interface WidgetConfig {
    widgetConfig: Partial<Widget>;
}
interface LibrarySetupStatusResponse {
    has_widgets_library_installed: boolean;
}

interface FetchWidgetsParams extends FilterParams {
    type?: WidgetType;
}

interface FetchWidgetParams extends WidgetsParamsAccountId, WidgetsParamsWidgetId {}
interface DeleteWidgetParams extends WidgetsParamsAccountId, WidgetsParamsWidgetId {}
interface UpdateWidgetParams extends WidgetsParamsAccountId, WidgetsParamsWidgetId, WidgetConfig {}
interface CreateWidgetParams extends WidgetsParamsAccountId, WidgetConfig {}

interface CreateOrUpdateParams extends Omit<UpdateWidgetParams, 'widgetId'> {
    widgetId?: number;
}

const fetchWidgets = (accountId: number, params: FetchWidgetsParams = {}) =>
    Axios.get<PaginatedData<Widget>>(`/accounts/${accountId}/elements`, { params }).then(
        response => response.data,
    );

const fetchWidget = ({ accountId, widgetId }: FetchWidgetParams) =>
    Axios.get<Widget>(`/accounts/${accountId}/elements/${widgetId}`).then(
        response => response.data,
    );

const deleteWidget = ({ accountId, widgetId }: DeleteWidgetParams) =>
    Axios.delete<Widget>(`/accounts/${accountId}/elements/${widgetId}`).then(
        response => response.data,
    );

const createWidget = ({ accountId, widgetConfig }: CreateWidgetParams) =>
    Axios.post<Widget>(`/accounts/${accountId}/elements`, widgetConfig).then(
        response => response.data,
    );

const updateWidget = ({ accountId, widgetId, widgetConfig }: UpdateWidgetParams) => {
    return Axios.put<Widget>(`/accounts/${accountId}/elements/${widgetId}`, {
        ...widgetConfig,
        type: undefined,
    }).then(response => response.data);
};

const createOrUpdateWidget = ({ widgetId, ...sharedParams }: CreateOrUpdateParams) =>
    widgetId ? updateWidget({ widgetId: widgetId, ...sharedParams }) : createWidget(sharedParams);

const fetchLibrarySetupStatus = ({ accountId }: { accountId: number }) =>
    Axios.get<LibrarySetupStatusResponse>(`/accounts/${accountId}/widgets/status`).then(
        response => response.data,
    );

const fetchCampaignWidgets = (accountId: number, campaignId: number) =>
    Axios.get<Widget[]>(`/accounts/${accountId}/campaigns/${campaignId}/widgets`).then(
        response => response.data,
    );

export {
    fetchWidget,
    fetchWidgets,
    fetchCampaignWidgets,
    fetchLibrarySetupStatus,
    createWidget,
    createOrUpdateWidget,
    deleteWidget,
    updateWidget,
};
export type { FetchWidgetsParams };
